import { Show, createEffect, createSignal, onCleanup } from "solid-js";
import { scanQrFromCamera } from "../../services/qrScanner";
import { styles } from "../../styles";
import { FriendCoordinateSelect } from "./FriendCoordinateSelect";
import type { FriendCodeRegistration, FriendCoordinateOption, FriendCoordinateReference } from "./types";

type Props = {
  open: boolean;
  options: FriendCoordinateOption[];
  onClose: () => void;
  onRegister: (registration: FriendCodeRegistration) => void;
};

export function FriendRegistrationModal(props: Props) {
  const [playerId, setPlayerId] = createSignal("");
  const [playerName, setPlayerName] = createSignal("");
  const [photoSeq, setPhotoSeq] = createSignal("");
  const [friendImageUrl, setFriendImageUrl] = createSignal("");
  const [coordinate, setCoordinate] = createSignal<FriendCoordinateReference>(null);
  const [notWeb, setNotWeb] = createSignal(false);
  const [registrationTab, setRegistrationTab] = createSignal<"qr" | "json">("qr");
  const [state, setState] = createSignal<"form" | "reading" | "error">("form");
  const [error, setError] = createSignal("");
  let video!: HTMLVideoElement;
  let controller: AbortController | undefined;

  const stopReading = () => {
    controller?.abort();
    controller = undefined;
  };
  const close = () => {
    stopReading();
    props.onClose();
  };
  const startReading = async () => {
    if (controller) return;
    if (playerId().trim() && !/^\d+$/.test(playerId().trim())) {
      setError("プレイヤーIDは数字で入力してください。");
      setState("error");
      return;
    }
    if (photoSeq().trim() && !/^\d+$/.test(photoSeq().trim())) {
      setError("photo_seqは数字で入力してください。");
      setState("error");
      return;
    }
    if (!playerName().trim()) {
      setError("プレイヤー名を入力してください。");
      setState("error");
      return;
    }
    controller = new AbortController();
    setError("");
    setState("reading");
    try {
      await new Promise<void>((resolve) => queueMicrotask(resolve));
      const qr = await scanQrFromCamera(video, controller.signal);
      props.onRegister({
        playerId: playerId(),
        playerName: playerName(),
        photoSeq: photoSeq(),
        coordinate: coordinate(),
        qr,
        tags: notWeb() ? { not_web: true } : {},
        imageUrl: friendImageUrl(),
      });
      close();
    } catch (cause) {
      if (cause instanceof DOMException && cause.name === "AbortError") return;
      setError(cause instanceof Error ? cause.message : String(cause));
      setState("error");
    } finally {
      controller = undefined;
    }
  };

  createEffect(() => {
    if (!props.open) {
      stopReading();
      return;
    }
    setPlayerId("");
    setPlayerName("");
    setPhotoSeq("");
    setFriendImageUrl("");
    setCoordinate(null);
    setNotWeb(false);
    setRegistrationTab("qr");
    setState("form");
    setError("");
  });
  onCleanup(stopReading);

  return (
    <Show when={props.open}>
      <div class={styles.qrRegistrationLayer} role="presentation">
        <button class={styles.previewBackdrop} type="button" aria-label="フレンドコード登録を閉じる" onClick={close} />
        <section class={styles.friendRegistrationModal} role="dialog" aria-modal="true" aria-labelledby="friend-registration-title">
          <header class={styles.previewHeader}>
            <h2 id="friend-registration-title">フレンドコード登録</h2>
            <button class={styles.iconButton} type="button" aria-label="閉じる" onClick={close}>×</button>
          </header>
          <div class={styles.friendRegistrationTabs} role="tablist" aria-label="フレンドコードの登録方法">
            <button
              class={styles.friendRegistrationTab}
              classList={{ [styles.friendRegistrationTabActive]: registrationTab() === "qr" }}
              type="button"
              role="tab"
              aria-selected={registrationTab() === "qr"}
              disabled={state() === "reading"}
              onClick={() => setRegistrationTab("qr")}
            >QRから登録</button>
            <button
              class={styles.friendRegistrationTab}
              classList={{ [styles.friendRegistrationTabActive]: registrationTab() === "json" }}
              type="button"
              role="tab"
              aria-selected={registrationTab() === "json"}
              disabled={state() === "reading"}
              onClick={() => {
                setRegistrationTab("json");
                setError("");
              }}
            >JSONから登録</button>
          </div>
          <Show
            when={registrationTab() === "qr"}
            fallback={<div class={styles.friendConstruction}><p>工事中です</p></div>}
          >
          <div class={styles.friendRegistrationBody}>
            <Show when={state() === "form" || state() === "error"}>
              <label class={styles.field}>
                <span class={styles.label}>プレイヤーID（任意）</span>
                <input class={styles.input} inputmode="numeric" value={playerId()} onInput={(event) => setPlayerId(event.currentTarget.value)} placeholder="例: 242506" />
              </label>
              <label class={styles.field}>
                <span class={styles.label}>プレイヤー名</span>
                <input class={styles.input} value={playerName()} maxlength={50} onInput={(event) => setPlayerName(event.currentTarget.value)} placeholder="例: ★まみ★" />
              </label>
              <label class={styles.field}>
                <span class={styles.label}>photo_seq（任意）</span>
                <input class={styles.input} inputmode="numeric" value={photoSeq()} onInput={(event) => setPhotoSeq(event.currentTarget.value)} placeholder="例: 1" />
              </label>
              <label class={styles.field}>
                <span class={styles.label}>画像URL（任意）</span>
                <input
                  class={styles.input}
                  type="url"
                  value={friendImageUrl()}
                  onInput={(event) => setFriendImageUrl(event.currentTarget.value)}
                  placeholder="https://example.com/friend-card.jpg"
                  autocomplete="url"
                />
              </label>
              <div class={styles.field}>
                <span class={styles.label}>コーデ（後から設定可能）</span>
                <FriendCoordinateSelect options={props.options} value={coordinate()} onChange={setCoordinate} />
              </div>
              <label class={styles.settingCheck}>
                <input type="checkbox" checked={notWeb()} onChange={(event) => setNotWeb(event.currentTarget.checked)} />
                <span>webフレカではない</span>
              </label>
            </Show>
            <Show when={state() === "reading"}>
              <video ref={video} class={styles.qrCameraPreview} playsinline muted />
              <p>QRをカメラ中央に映してください…</p>
            </Show>
            <Show when={error()}><p class={styles.qrRegistrationError} role="alert">{error()}</p></Show>
            <div class={styles.qrRegistrationActions}>
              <Show when={state() === "form" || state() === "error"}>
                <button class={styles.mixRegistrationButton} type="button" onClick={() => void startReading()}>QRを読み取って登録</button>
              </Show>
              <button class={styles.secondaryButton} type="button" onClick={close}>閉じる</button>
            </div>
          </div>
          </Show>
        </section>
      </div>
    </Show>
  );
}
